import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Alert, Button, Spin, Typography } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import RegistrationBox from "./RegistrationBox";
import AgentRegisterShell from "./agentRegister/AgentRegisterShell";
import AgentRegisterBenefits from "./agentRegister/AgentRegisterBenefits";
import {
  getSubscriptionSetupPreview,
  postSubscriptionSetupBillingSession,
  SUBSCRIPTION_SETUP_TOKEN_KEY,
} from "../../services/subscriptionSetupApi";
import { formatPackageSubscriptionGrossPrice } from "../../utils/resolvePackagePriceWithVat";

const { Title, Paragraph, Text } = Typography;

function _dbg(msg, data) {
  void msg;
  void data;
}

const priceLabel = (netAmount) => {
  const n = Number(netAmount);
  if (!Number.isFinite(n) || n <= 0) return "ללא עלות";
  return formatPackageSubscriptionGrossPrice(n);
};

const SubscriptionBillingPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("subscriptionToken") || "";

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoading(false);
      setError("חסר קוד הקישור. חזרו למייל/הודעה שקיבלתם ונסו שוב.");
      _dbg("mount_missing_token", {});
      return () => {};
    }
    _dbg("mount_fetch_preview", { tokenLen: token.length });
    (async () => {
      try {
        const res = await getSubscriptionSetupPreview(token);
        if (cancelled) return;
        if (!res || res.ok === false) {
          setError(
            res?.error === "invalid_token"
              ? "קישור לא חוקי או שפג תוקפו. יש ליצור קישור חדש מול מנהל המערכת."
              : "אירעה שגיאה בטעינת פרטי הקישור.",
          );
          _dbg("preview_error", { error: res?.error });
          return;
        }
        setPreview(res);
        _dbg("preview_ok", {
          valid: res.valid,
          alreadyUsed: res.alreadyUsed,
          targetCode: res?.targetPackage?.code,
          currentCode: res?.currentPackage?.code,
        });
      } catch (e) {
        if (!cancelled) {
          setError("אירעה שגיאה בטעינת פרטי הקישור.");
          _dbg("preview_exception", { err: String(e).slice(0, 120) });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const notUsable = useMemo(() => {
    if (!preview) return null;
    if (preview.alreadyUsed) return "קישור זה כבר נוצל. יש ליצור קישור חדש מול מנהל המערכת.";
    if (preview.valid === false) return "הקישור אינו בתוקף. יש ליצור קישור חדש מול מנהל המערכת.";
    return null;
  }, [preview]);

  async function handleContinue() {
    setSubmitting(true);
    setError("");
    try {
      try {
        sessionStorage.setItem(SUBSCRIPTION_SETUP_TOKEN_KEY, token);
      } catch {
        /* private mode */
      }
      _dbg("billing_session_request", {});
      const res = await postSubscriptionSetupBillingSession(token);
      if (!res || res.ok === false || !res.paymentUrl) {
        const msg =
          res?.error === "token_already_used"
            ? "קישור זה כבר נוצל."
            : res?.error === "token_expired"
              ? "הקישור פג תוקף."
              : res?.error === "invalid_token"
                ? "קישור לא חוקי."
                : res?.message || "לא הצלחנו ליצור דף תשלום כעת. נסו שוב בעוד רגע.";
        setError(msg);
        _dbg("billing_session_error", { error: res?.error });
        return;
      }
      _dbg("redirecting_to_hyp", { host: new URL(res.paymentUrl).host });
      window.location.assign(res.paymentUrl);
    } catch (e) {
      const apiErr =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "שגיאת שרת";
      setError(String(apiErr));
      _dbg("billing_session_exception", { err: String(apiErr).slice(0, 120) });
    } finally {
      setSubmitting(false);
    }
  }

  const aside = <AgentRegisterBenefits />;

  const body = (() => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin size="large" />
        </div>
      );
    }
    if (error && !preview) {
      return (
        <Alert
          type="error"
          showIcon
          message="לא ניתן להמשיך"
          description={error}
        />
      );
    }
    if (notUsable) {
      return (
        <Alert
          type="warning"
          showIcon
          message="הקישור לא זמין לשימוש"
          description={notUsable}
        />
      );
    }

    const fullName = preview?.agent?.fullName || "";
    const currentCode = preview?.currentPackage?.code || "—";
    const targetCode = preview?.targetPackage?.code || "—";
    const targetName = preview?.targetPackage?.name || targetCode;
    const monthly = preview?.targetPackage?.monthlyAmount;

    return (
      <>
        {fullName ? (
          <Paragraph style={{ marginBottom: 4 }}>
            <Text strong>סוכן: </Text>
            <Text>{fullName}</Text>
          </Paragraph>
        ) : null}

        <Paragraph style={{ marginBottom: 4 }}>
          <Text strong>תוכנית נוכחית: </Text>
          <Text>{currentCode}</Text>
        </Paragraph>

        <Title level={4} style={{ marginTop: 12 }}>
          {targetName}
        </Title>
        <Paragraph style={{ marginBottom: 4 }}>
          <Text strong>תוכנית יעד: </Text>
          <Text>{targetCode}</Text>
        </Paragraph>
        <Paragraph style={{ marginBottom: 12 }}>
          <Text strong>מחיר חודשי: </Text>
          <Text>{priceLabel(monthly)}</Text>
        </Paragraph>

        <Alert
          type="info"
          showIcon
          message="הסדרת אמצעי תשלום לשדרוג מנוי"
          description="לחיצה על הכפתור תעביר אותך לדף תשלום מאובטח של Hyp לשמירת אמצעי תשלום. לא מזינים כרטיס באתר HotelsRiver ולא נשמרים פרטי כרטיס אצלנו. התוכנית תופעל אוטומטית לאחר שמירת אמצעי התשלום."
          style={{ marginBottom: 12 }}
        />

        {error ? (
          <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
        ) : null}

        <Button
          type="primary"
          size="large"
          icon={<CheckOutlined />}
          loading={submitting}
          onClick={handleContinue}
          style={{ width: "100%" }}
        >
          המשך לשמירת אמצעי תשלום
        </Button>
      </>
    );
  })();

  return (
    <AgentRegisterShell variant="split" aside={aside}>
      <RegistrationBox title="שדרוג מנוי · אמצעי תשלום">{body}</RegistrationBox>
      <div style={{ textAlign: "center", marginTop: 12 }}>
        <Button type="link" onClick={() => navigate("/")}>חזרה לעמוד הבית</Button>
      </div>
    </AgentRegisterShell>
  );
};

export default SubscriptionBillingPage;
